import { motion } from "framer-motion";
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import brandStatementImage from "@/assets/brand-statement-bg.png";

const BrandStatement = () => {
  const scrollToCalendly = useCallback(() => {
    const calendlySection = document.getElementById("calendly");
    if (calendlySection) {
      calendlySection.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <section className="py-20 md:py-28 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #030712 0%, #0a1628 100%)" }}>
      {/* Subtle decorative element */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl" style={{ backgroundColor: "rgba(37, 99, 235, 0.15)" }} />
      </div>

      <div className="container px-4 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="order-2 flex justify-center"
          >
            <div className="relative">
              {/* Decorative glow */}
              <div className="absolute -inset-4 rounded-2xl blur-2xl opacity-30" style={{ background: "linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, transparent 50%, rgba(37, 99, 235, 0.15) 100%)" }} />
              
              {/* Image container */}
              <div className="relative w-72 h-96 md:w-80 md:h-[450px] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                <img 
                  src={brandStatementImage} 
                  alt="Capital de Marca" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Subtle overlay */}
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(3, 7, 18, 0.3) 0%, transparent 50%)" }} />
              </div>
            </div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="order-1"
          >
            {/* Main statement */}
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-white leading-tight mb-6 tracking-tight">
              Si no dominás la percepción,{" "}
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(135deg, #60a5fa 0%, #93c5fd 100%)" }}
              >
                no dominas el mercado.
              </span>
            </h2>

            {/* Elegant divider */}
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="w-20 h-px mb-6 origin-left"
              style={{ background: "linear-gradient(to right, hsl(214, 95%, 77% / 0.6), transparent)" }}
            />

            {/* Description */}
            <div className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 space-y-5">
              <p>No vamos a inventar una versión más atractiva de tu empresa.</p>
              <p>Vamos a identificar el valor que ya construiste, comprender qué parte de ese valor todavía no está siendo percibido y definir cómo llevarlo a la posición que merece.</p>
              <p>Para que tu marca se posicione, se diferencie y sea elegida por el valor que realmente tiene.</p>
            </div>

            {/* CTA Button */}
            <Button
              variant="gold"
              size="xl"
              onClick={scrollToCalendly}
              className="tracking-wider font-semibold rounded-full group"
            >
              AGENDAR LLAMADA
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BrandStatement;
